<template>
  <section class="section-base form-base">
    <template v-if="getAdStLoaded">
      <AddStatementHeader
        :title="title"
        :steps-count="stepsCount"
        :current-step="stepsCount"
      />

      <v-row>
        <v-col cols="12" v-if="hasEditedStatement">
          <v-textarea
            v-model="$v.changeDescription.$model"
            :errorMessages="getValidationErrorText($v.changeDescription)"
            outlined
            label="Описание внесенных изменений"
          />
        </v-col>
        <v-col cols="12">
          <UploadDocumentAlt
            v-if="typeDocument"
            :documents="$v.documents.$model"
            :typeDocument="typeDocument"
            :errorMessages="getValidationErrorText($v.documents)"
            @change="changeDocuments($event)"
          />
        </v-col>
      </v-row>

      <div class="form-base__footer pt-10">
        <div class="button-box justify-space-between">
          <v-btn
            depressed
            color="primary"
            class="button-base"
            :loading="loading"
            @click="prevBtnClickHandler"
            >Назад
          </v-btn>

          <v-btn
            depressed
            color="primary"
            class="button-base"
            :loading="loading"
            @click="sendPackage"
            >Подписать и отправить
          </v-btn>
        </div>
      </div>
      <SignatureComponent />
    </template>
    <SkeletonLoader v-if="getAdStLoading" />
  </section>
</template>

<script>
import UploadDocumentAlt from '@/components/base/UploadDocumentAlt';
import SignatureComponent from '@/components/base/SignatureComponent';

import { ServicesModule } from '@/store/modules/services';
import { AddStModule } from '@/store/modules/alternativeServices/additionalStatement';
import { getValidationErrorText } from '@/utils/validate';
import { mapStatementName } from '@/utils/services';
import { requiredIf } from 'vuelidate/lib/validators';
import { RouteName } from '@/router';
import { Role } from '@/utils/dictionaries';
import cloneDeep from 'clone-deep';
import SkeletonLoader from '@/components/base/SkeletonLoader';
import AddStatementHeader from '@/views/alternativeServices/AddStatementHeader';
import {
  addDocumentsStepsCount,
  addDocumentsWithEdit,
  getAddDocumentsBreadcrumbs,
} from '@/utils/common';
import { BreadCrumbsModule } from '@/store/modules/breadCrumbs';

export default {
  name: 'AddDocumentsSign',
  props: {
    altStatementId: {
      type: String,
      required: true,
    },
  },
  components: {
    UploadDocumentAlt,
    SignatureComponent,
    SkeletonLoader,
    AddStatementHeader,
  },
  data() {
    return {
      getValidationErrorText,
      documents: [],
      changeDescription: '',
      typeDocument: null,
    };
  },
  computed: {
    title() {
      return mapStatementName[this.addStatement.originalStatementType];
    },

    stepsCount() {
      return addDocumentsStepsCount(this.withEdit);
    },

    withEdit() {
      return addDocumentsWithEdit(this.addStatement);
    },

    loading() {
      return (
        AddStModule.saveDocumentsState.loading ||
        AddStModule.stXmlBase64.loading ||
        AddStModule.sendPackageState.loading
      );
    },

    hasEditedStatement() {
      return this.addStatement.changedStatementId;
    },

    role() {
      if (this.addStatement.applicant.person) return Role.PERSON;
      if (this.addStatement.applicant.organization) return Role.ORGANIZATION;
      if (this.addStatement.applicant.foreignOrganization)
        return Role.FOREIGN_ORGANIZATION;

      throw new Error('Нет заявителя');
    },

    trusteeInfo() {
      return this.addStatement.applicant[this.role].trusteeInfo;
    },

    assignee() {
      return this.addStatement.applicant[this.role].assignee;
    },

    addStatement() {
      return AddStModule.addStatement.entity;
    },

    getAdStLoading() {
      return AddStModule.addStatement.loading;
    },
    getAdStLoaded() {
      return AddStModule.addStatement.loaded;
    },
  },
  validations() {
    return {
      documents: {
        DOCUMENTS_MIN_LENGTH: requiredIf(() => !this.hasEditedStatement),
      },
      changeDescription: {
        required: requiredIf(() => this.hasEditedStatement),
      },
    };
  },
  async created() {
    await AddStModule.getAddStById(this.altStatementId);

    if (this.addStatement?.documents?.length) {
      this.documents = [...this.addStatement.documents];
    }

    this.typeDocument = this.addStatement.addStatementType;

    BreadCrumbsModule.SET(
      getAddDocumentsBreadcrumbs(this.addStatement.originalStatementId)
    );
  },
  beforeDestroy() {
    BreadCrumbsModule.SET(null);
  },
  methods: {
    prevBtnClickHandler() {
      const prevRouteName = this.withEdit
        ? RouteName.ADD_DOCUMENTS_EDIT
        : RouteName.ADD_DOCUMENTS_APPLICANT;

      this.$router.push({
        name: prevRouteName,
      });
    },

    changeDocuments(event) {
      this.documents = event;
      this.saveDocuments();
    },

    async saveDocuments() {
      await AddStModule.saveDocuments({
        id: this.addStatement.id,
        documents: this.documents,
        changeDescription: this.hasEditedStatement
          ? this.changeDescription
          : null,
      });
    },

    async sendPackage() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        await this.$vuetify.goTo('#scroll-target', { duration: 200 });
        return;
      }

      try {
        if (this.hasEditedStatement) await this.saveDocuments();

        await AddStModule.getStXmlBase64(this.addStatement.id);

        const runSignProps = [
          { xmlSigBase64: AddStModule.stXmlBase64.entity },
          this.getDataUserCertificate(this.addStatement.applicant),
        ];

        // подписываем XmlBase64 и получаем подпись в Base64
        const signs = await ServicesModule.runSign(...runSignProps);

        await AddStModule.sendPackage({
          addStatementId: this.addStatement.id,
          xmlSigBase64: signs.xmlSigBase64[0],
        });
        this.$router.replace({
          name: RouteName.STATEMENT_DETAIL,
          params: {
            id: this.addStatement.originalStatementId,
          },
        });
      } catch (error) {
        await this.$vuetify.goTo('#header-target', { duration: 200 });
      }
    },

    getDataUserCertificate(applicant) {
      const subject = cloneDeep(applicant);

      subject.trustee = this.trusteeInfo;
      subject.assignee = this.assignee;

      return subject;
    },
  },
};
</script>
